import { ChangeDetectorRef, Component, DestroyRef, ElementRef, inject, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatSelectChange } from '@angular/material/select';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, finalize } from 'rxjs/operators';

import { ToastLevel } from 'src/app/shared/models/toast-message.module';
import { ToastService } from 'src/app/shared/services/toast.service';

import { EmailService } from '../../../../core/api/email.service';
import { EmailTemplate } from '../../../../shared/models';
import { SpinnerService } from '../../../../shared/spinner/spinner.service';

@Component({
  selector: 'fc-email-templates',
  templateUrl: './email-template-details.component.html',
  styleUrls: ['./email-template-details.component.scss'],
})
export class EmailTemplateDetailsComponent implements OnInit {
  destroyRef = inject(DestroyRef);
  template: EmailTemplate;
  templateForm: FormGroup = this.formBuilder.group({
    body: new FormControl('', [Validators.required]),
    description: new FormControl('', [Validators.required]),
    name: new FormControl('', [Validators.required]),
    subject: new FormControl('', [Validators.required]),
  });
  templateId?: number;

  @ViewChild('templateFrame') templateFrame: ElementRef;
  availableSubs: string[];

  constructor(
    readonly changeDetector: ChangeDetectorRef,
    readonly formBuilder: FormBuilder,
    readonly emailService: EmailService,
    readonly route: ActivatedRoute,
    readonly router: Router,
    readonly spinnerService: SpinnerService,
    readonly toastService: ToastService
  ) {
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: queryParams => {
        this.templateId = +queryParams.get('templateId');
      },
    });
    this.templateForm.controls.body.valueChanges
      .pipe(debounceTime(1000), takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (body: string) => {
          this.setTemplateBody(body);
        },
      });
  }

  ngOnInit() {
    if (this.templateId) {
      this.emailService.getEmailTemplate(this.templateId).subscribe({
        next: (template: EmailTemplate) => {
          this.availableSubs = [
            ...(template.requiredSubstitution || []),
            ...(template.optionalSubstitution || []),
          ].sort((a, b) => a.localeCompare(b));
          this.setTemplateData(template);
        },
      });
    }
  }

  setTemplateData(template: EmailTemplate) {
    this.template = template;
    this.templateForm.controls.body.setValue(template.body, {
      emitEvent: false,
    });
    this.templateForm.controls.description.setValue(template.description, {
      emitEvent: false,
    });
    this.templateForm.controls.name.setValue(template.name, {
      emitEvent: false,
    });
    this.templateForm.controls.subject.setValue(template.subject, {
      emitEvent: false,
    });
    this.changeDetector.detectChanges();
    this.setTemplateBody(template.body);
  }

  saveTemplate(): void {
    this.spinnerService.show('email-templates-spinner');
    if (this.templateId) {
      this.emailService
        .updateEmailTemplate(this.templateId, this.templateForm.value)
        .pipe(finalize(() => this.spinnerService.hide('email-templates-spinner')))
        .subscribe({
          next: (template: EmailTemplate) => {
            this.setTemplateData(template);
            this.toastService.sendMessage('Email template updated', ToastLevel.SUCCESS);
            this.router.navigate(['admin', 'manage', 'email', 'template']);
          },
          error: err => {
            this.toastService.sendMessage(`Error updating email template: ${err}`, ToastLevel.WARN);
          },
          complete: () => {},
        });
    } else {
      this.emailService
        .createEmailTemplate(this.templateForm.value)
        .pipe(finalize(() => this.spinnerService.hide('email-templates-spinner')))
        .subscribe({
          next: (template: EmailTemplate) => {
            this.setTemplateData(template);
            this.router.navigate(['admin', 'manage', 'email', 'template']);
            this.toastService.sendMessage('Email template created', ToastLevel.SUCCESS);
          },
          error: err => {
            this.toastService.sendMessage(`Error creating email template: ${err}`, ToastLevel.WARN);
          },
          complete: () => {},
        });
    }
  }

  setTemplateBody(body: string): void {
    const iframe = this.templateFrame.nativeElement;
    iframe.onload = () => {
      let doc = iframe.contentWindow.document;
      doc.open();
      doc.write(body);
      doc.close();
      iframe.onload = null;
    };
    iframe.src = 'about:blank';
  }

  hasSub(requiredSub: string) {
    return this.templateForm.controls.body.value.includes(requiredSub);
  }

  addSub($event: MatSelectChange) {
    this.templateForm.controls.body.setValue(this.templateForm.controls.body.value + $event.value);
    $event.source.value = '';
  }

  isValid() {
    let isValid = !this.templateForm.invalid;
    this.template?.requiredSubstitution?.forEach(sub => {
      if (!this.hasSub(sub)) {
        isValid = false;
      }
    });
    return isValid;
  }
}
